/**
 * De route van één kar: de stapblokken naast elkaar, strak tegen elkaar zonder gaps.
 *
 * Drie dingen die uit de stakeholderpunten volgen:
 *  - de kop van elk blok is volledig ingekleurd in de kleur van de bewerking;
 *  - **afstrepen verandert de kleur niet** — een afgeronde stap houdt zijn kleur en
 *    krijgt verlaagde opacity, een vinkje en de naam van de uitvoerder;
 *  - winkelen, controle en gereed blijven leeg, maar staan er wél als stap.
 *
 * Klikken op een stap opent het stap-menu met de opties en de informatie per
 * onderdeel; zo hoeft er niets extra's in het blok zelf te staan.
 */
import type { Weergavestap } from '../api';
import { tijd } from '../gegevens';
import { IconCamera, IconVink } from './Iconen';

interface Props {
  route: Weergavestap[];
  positie: number;
  onStapKiezen: (stap: Weergavestap) => void;
  /** Toon de genormeerde tijd per stap. Nooit voor productie. */
  metUren?: boolean;
}

export function Routepaneel({ route, positie, onStapKiezen, metUren }: Props) {
  return (
    <div className="hs-route">
      {route.map((stap, i) => {
        const gereed = i < positie;
        const nu = i === positie;
        return (
          <button
            key={stap.sleutel}
            type="button"
            className={`hs-stap${gereed ? ' hs-stap--gereed' : ''}${nu ? ' hs-stap--nu' : ''}`}
            style={{ textAlign: 'left', border: 0, padding: 0, cursor: 'pointer', font: 'inherit' }}
            onClick={() => onStapKiezen(stap)}
            title={`${stap.kolomnaam} — klik voor opties en informatie`}
          >
            <span
              className="hs-stap__kop"
              style={{ background: stap.kleur, color: stap.tekstkleur ?? tekstOp(stap.kleur) }}
            >
              {stap.kolomnaam}
              {stap.verplaatst ? ' ⇄' : ''}
            </span>
            <span className="hs-stap__body">
              {/* Winkelen, controle en gereed hebben geen waarde en blijven leeg: alleen
                  de gekleurde kop. Hun toelichting staat in het stap-menu. */}
              {stap.label ? <span className="hs-stap__waarde">{stap.label}</span> : null}

              <span
                className="hs-stap__status"
                style={{
                  color: gereed ? 'var(--goed-tekst)' : nu ? 'var(--primair)' : 'var(--muted-licht)',
                  marginTop: 'auto',
                }}
              >
                {gereed ? (
                  <>
                    <IconVink maat={10} /> afgestreept · {stap.afgestreeptDoor}
                  </>
                ) : nu ? (
                  '● staat hier nu'
                ) : (
                  'wacht'
                )}
                {/* Verschillende maten op één kar: de waarde hierboven is die van het
                    grootste onderdeel, de rest staat in het stap-menu. */}
                {stap.varianten > 1 ? (
                  <span className="hs-muted-licht"> · {stap.varianten} maten</span>
                ) : null}
              </span>

              {stap.fotoVerplicht && !gereed ? (
                <span
                  className="hs-stap__status"
                  style={{ color: 'var(--fout-tekst)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                >
                  <IconCamera maat={10} /> foto verplicht
                </span>
              ) : null}
              {metUren && stap.normMinuten ? (
                <span className="hs-mono hs-muted-licht" style={{ fontSize: 9.5 }}>
                  {String(stap.normMinuten).replace('.', ',')} min
                </span>
              ) : null}
              {stap.opmerking ? (
                <span className="hs-muted" style={{ fontSize: 9.5, fontStyle: 'italic' }}>
                  {stap.opmerking}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * De voetstrip onder een karkaart: dezelfde route, maar compact.
 */
export function Routestrip({ route, positie }: { route: Weergavestap[]; positie: number }) {
  return (
    <div className="hs-voetstrip">
      {route.map((stap, i) => {
        const gereed = i < positie;
        const nu = i === positie;
        return (
          <div
            key={stap.sleutel}
            className="hs-voetstrip__vak"
            style={{
              opacity: gereed ? 0.62 : 1,
              outline: nu ? '2px solid var(--primair)' : undefined,
              outlineOffset: -2,
            }}
          >
            <div
              className="hs-voetstrip__kop"
              style={{ background: stap.kleur, color: stap.tekstkleur ?? tekstOp(stap.kleur) }}
              title={stap.kolomnaam}
            >
              {stap.kolomnaam}
            </div>
            <div className="hs-voetstrip__status">
              {gereed ? (
                <span style={{ color: 'var(--goed-tekst)' }}>
                  ✓ {stap.afgestreeptDoor} · {tijd(stap.afgestreeptOp)}
                </span>
              ) : nu ? (
                <span style={{ color: 'var(--primair)', fontWeight: 600 }}>● nu hier</span>
              ) : (
                (stap.label || '—')
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Leesbare tekstkleur op een achtergrondkleur.
 *
 * De API stuurt de tekstkleur per stap mee (`tekstkleur`); dit is de terugval voor
 * plekken waar alleen een achtergrondkleur bekend is, en voor karren die nog van vóór
 * dat veld komen. Relatieve helderheid volgens WCAG — nodig omdat de
 * bewerkingskleuren van wit (Winkelen) tot zwart (Buigen) lopen.
 */
export function tekstOp(achtergrond: string): string {
  const hex = achtergrond.replace('#', '');
  if (hex.length !== 6) return '#ffffff';
  const kanaal = (deel: string) => {
    const v = parseInt(deel, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const helderheid =
    0.2126 * kanaal(hex.slice(0, 2)) + 0.7152 * kanaal(hex.slice(2, 4)) + 0.0722 * kanaal(hex.slice(4, 6));
  return helderheid > 0.45 ? '#1a1a1a' : '#ffffff';
}
